import { useRef, useState, useEffect } from "react";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, Copy, CheckCircle, Info, Palette, Save } from "lucide-react";
import { jsPDF } from "jspdf";

type QrFrame = "none" | "simple" | "rounded" | "dotted";

const FRAME_OPTIONS: { id: QrFrame; label: string; desc: string }[] = [
  { id: "none",    label: "None",       desc: "Clean QR, no border" },
  { id: "simple",  label: "Simple",     desc: "Border + SCAN ME label" },
  { id: "rounded", label: "Rounded",    desc: "Card + Scan to connect" },
  { id: "dotted",  label: "Dotted",     desc: "Dotted border" },
];

interface QrCodeDisplayProps {
  username: string;
  customQrColour?: string | null;
  qrBgColor?: string | null;
  qrFrame?: string | null;
  avatarUrl?: string | null;
}

export default function QrCodeDisplay({ username, customQrColour, qrBgColor, qrFrame, avatarUrl }: QrCodeDisplayProps) {
  const qrRef   = useRef<HTMLDivElement>(null);
  const [copied,  setCopied]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [fgColor, setFgColor] = useState(customQrColour ?? "#000000");
  const [bgColor, setBgColor] = useState(qrBgColor      ?? "#ffffff");
  const [frame,   setFrame]   = useState<QrFrame>((qrFrame as QrFrame) ?? "none");

  useEffect(() => { if (customQrColour) setFgColor(customQrColour); }, [customQrColour]);
  useEffect(() => { if (qrBgColor)      setBgColor(qrBgColor);      }, [qrBgColor]);
  useEffect(() => { if (qrFrame)        setFrame(qrFrame as QrFrame); }, [qrFrame]);

  const profileUrl = `https://commonqr.com/${username}`;

  const saveColors = async () => {
    setSaving(true);
    try {
      await fetch("/api/v1/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ customQrColour: fgColor, qrBgColor: bgColor, qrFrame: frame }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save QR settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const downloadPNG = () => {
    const a = document.createElement("a");
    a.href = `/api/v1/qr/${username}`;
    a.download = `CommonQR_${username}.png`;
    a.click();
  };

  const downloadSVG = () => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.download = `CommonQR_${username}.svg`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    const imgUrl = `/api/v1/qr/${username}`;
    const res = await fetch(imgUrl);
    const blob = await res.blob();
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1];
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const qrW = 100;
      const qrX = (pageW - qrW) / 2;
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('CommonQR', pageW / 2, 30, { align: 'center' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      pdf.text('One QR. Your Permanent Digital Identity. Forever.', pageW / 2, 38, { align: 'center' });
      pdf.addImage(base64, 'PNG', qrX, 50, qrW, qrW);
      pdf.setFontSize(11);
      pdf.setTextColor(0);
      pdf.text(`commonqr.com/${username}`, pageW / 2, 158, { align: 'center' });
      pdf.save(`CommonQR_${username}.pdf`);
    };
    reader.readAsDataURL(blob);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const QR_SIZE = 220;
  const LOGO_SIZE = Math.round(QR_SIZE * 0.15);

  const frameWrapperClass: Record<QrFrame, string> = {
    none:    "p-3 rounded-xl",
    simple:  "p-6 rounded-lg border-4",
    rounded: "p-6 rounded-3xl border-8",
    dotted:  "p-6 rounded-xl border-4 border-dashed",
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-4">
        <h2 className="text-sm font-semibold">Your QR Code</h2>
        <p className="text-muted-foreground text-sm mt-1">Anyone who scans this code reaches your live profile instantly.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        {/* ── Left: QR, link, downloads ── */}
        <div className="flex flex-col items-center">
          <div className="bg-white p-4 rounded-2xl shadow-lg shadow-primary/10 border border-border/50 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent pointer-events-none" />
            <div
              ref={qrRef}
              className={`relative z-10 ${frameWrapperClass[frame]}`}
              style={{
                backgroundColor: bgColor,
                borderColor: frame !== "none" ? fgColor : undefined,
              }}
            >
              <QRCode
                value={profileUrl}
                size={QR_SIZE}
                level="H"
                fgColor={fgColor}
                bgColor={bgColor}
              />
              {avatarUrl && (
                <img
                  src={avatarUrl}
                  alt="avatar"
                  style={{
                    position:     "absolute",
                    top:          "50%",
                    left:         "50%",
                    transform:    "translate(-50%, -50%)",
                    width:        LOGO_SIZE,
                    height:       LOGO_SIZE,
                    borderRadius: "50%",
                    border:       "2px solid white",
                    objectFit:    "cover",
                    pointerEvents: "none",
                  }}
                />
              )}
              {frame === "simple" && (
                <p className="text-center mt-2 text-xs font-bold tracking-widest" style={{ color: fgColor }}>SCAN ME</p>
              )}
              {frame === "rounded" && (
                <p className="text-center mt-2 text-xs font-semibold tracking-wide" style={{ color: fgColor }}>Scan to connect</p>
              )}
            </div>
          </div>

          {/* URL label */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm font-mono text-muted-foreground font-medium tracking-tight">
              commonqr.com/<span className="text-primary font-semibold">{username}</span>
            </span>
            <button
              onClick={copyUrl}
              className="p-1 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-primary"
              title="Copy link"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>

          {/* Action Buttons */}
          <div className="mt-4 grid w-full max-w-sm grid-cols-2 gap-2">
            <Button
              onClick={downloadPNG}
              className="px-3 py-2 rounded-lg font-medium text-sm shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download PNG
            </Button>
            <Button
              onClick={downloadSVG}
              variant="outline"
              className="px-3 py-2 rounded-lg font-medium text-sm border hover:-translate-y-0.5 transition-all"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download SVG
            </Button>
            <Button
              onClick={downloadPDF}
              variant="outline"
              className="px-3 py-2 rounded-lg font-medium text-sm border hover:-translate-y-0.5 transition-all"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
            <a
              href={`/${username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-3 py-2 rounded-lg font-medium text-sm border border-primary text-primary hover:bg-primary/5 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" /> View Live Profile
            </a>
          </div>
        </div>

        {/* ── Right: customiser + tips ── */}
        <div className="flex flex-col gap-3">
          <div className="bg-muted/40 border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Customise Your QR</span>
            </div>

            {/* Colour pickers */}
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1.5 block">QR Colour</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="w-10 h-10 rounded-full border-2 border-border cursor-pointer p-0.5 bg-transparent"
                  />
                  <span className="text-xs font-mono text-muted-foreground">{fgColor}</span>
                </div>
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1.5 block">Background</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-10 h-10 rounded-full border-2 border-border cursor-pointer p-0.5 bg-transparent"
                  />
                  <span className="text-xs font-mono text-muted-foreground">{bgColor}</span>
                </div>
              </div>
            </div>

            {/* Frame picker */}
            <div className="mb-4">
              <label className="text-xs text-muted-foreground mb-2 block">Frame Style</label>
              <div className="grid grid-cols-2 gap-2">
                {FRAME_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setFrame(opt.id)}
                    className={`px-3 py-2 rounded-xl border-2 text-left transition-all ${
                      frame === opt.id
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground hover:border-primary/50"
                    }`}
                  >
                    <p className="text-xs font-semibold">{opt.label}</p>
                    <p className="text-xs mt-0.5 opacity-70">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Save button */}
            <button
              onClick={saveColors}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {saved ? (
                <><CheckCircle className="w-4 h-4" /> Saved!</>
              ) : (
                <><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}</>
              )}
            </button>
          </div>

          {/* Guidance note */}
          <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Print this QR on your business card, menu, packaging, or anywhere — it always stays up to date.</span>
          </div>

          {/* Tips */}
          <ul className="rounded-xl border border-border bg-muted/40 px-4 py-3 space-y-2">
            {[
              { tip: "Print on any material",     desc: "Business cards, banners, packaging, menus — all work." },
              { tip: "Update without reprinting",  desc: "Change your links any time. Your QR code stays the same." },
              { tip: "Track every scan",           desc: "See scan counts and activity in the Analytics tab." },
            ].map((t) => (
              <li key={t.tip} className="flex items-start gap-2 text-xs">
                <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />
                <span><span className="font-semibold text-foreground">{t.tip}.</span> <span className="text-muted-foreground">{t.desc}</span></span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
